// Angular imports
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// 3rd party imports
import {
  DxProgressBarComponent,
  DxValidationGroupComponent
} from 'devextreme-angular';

// Component imports
import {
  IconConstants,
  CCPasswordHelperService
} from '@therealmanage/ciranet-ui-common';
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// Service imports
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { UserService } from '../../_services/data-services/user.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';

// Constant imports
import { Constants } from '../../_constants/constants';

// Model imports
import { ResultStatusDto } from '../../_models/result-status-dto';
import { ResetPasswordParametersDto } from '../../_models/reset-password-parameters-dto';

// CC-Common imports
import { Utils, RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false
})
export class ResetPasswordComponent
  extends RecaptchaBaseComponent
  implements OnInit
{
  @ViewChild('resetPasswordValidationGroup', { static: false })
  resetPasswordValidationGroup: DxValidationGroupComponent;
  @ViewChild('passStrengthSuccess', { static: false })
  passStrengthSuccess: DxProgressBarComponent;

  loadingVisible = false;
  passwordMode: string;
  passwordShowButton: object;
  passwordHideButton: object;
  passwordStrength = 0;
  resetPasswordParameterDto = new ResetPasswordParametersDto();
  passwordRules: any[] = [];
  showErrorMessage = false;
  errorMessage = '';
  isPasswordReset = false;

  passwordSpecialCharactersAllowable =
    Constants.passwordSpecialCharactersAllowable;
  iconCircleCheck = IconConstants.CIRCLE_CHECK;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private passwordHelper: CCPasswordHelperService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService
  ) {
    super(errorService, envConfig);
  }

  ngOnInit(): void {
    this.initializePasswordRules();
    RxjsUtils.subscribeToQueryParams(this.route, (params) => {
      const token = params['token'];
      if (!!token && token.length > 0) {
        this.resetPasswordParameterDto.token = encodeURIComponent(token);
      } else {
        this.router.navigate(['user', 'login']);
      }
    });
    this.passwordMode = 'password';
    this.passwordShowButton = {
      icon: 'fas fa-eye',
      type: 'default',
      stylingMode: 'text',
      hint: 'Show Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.passwordHideButton = {
      icon: 'fas fa-eye-slash',
      type: 'default',
      stylingMode: 'text',
      hint: 'Hide Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
  }

  btnResetPasswordClick(): void {
    // Clear any previous error messages
    this.showErrorMessage = false;
    this.errorMessage = '';

    const validator = this.resetPasswordValidationGroup.instance.validate();
    if (validator.isValid) {
      if (this.resetPasswordParameterDto.newPassword.length < 8) {
        this.showErrorMessage = true;
        this.errorMessage = 'Password must have at least 8 characters.';
        return;
      }

      if (
        Utils.isFalse(
          this.passwordHelper.hasSpecialCharacter(
            this.resetPasswordParameterDto.newPassword
          )
        )
      ) {
        this.showErrorMessage = true;
        this.errorMessage =
          'Your password must contain at least one special character';
        return;
      }

      if (
        Utils.isFalse(
          this.passwordHelper.hasNumeric(
            this.resetPasswordParameterDto.newPassword
          )
        )
      ) {
        this.showErrorMessage = true;
        this.errorMessage = 'Your password must contain at least one number.';
        return;
      }

      if (this.passwordStrength < 5) {
        this.showErrorMessage = true;
        this.errorMessage =
          'Your password must include at least one lowercase letter and one uppercase letter.';
        return;
      } else if (this.isValidRecaptcha()) {
        this.resetPasswordParameterDto.recaptchaToken = this.recaptchaToken;
        this.resetPasswordParameterDto.confirmNewPassword =
          this.resetPasswordParameterDto.newPassword;
        this.doResetPassword().then(() => this.resetRecaptcha());
      } else {
        this.recaptchaErrorMessage = '';
        this.showErrorMessage = true;
        this.errorMessage =
          'Please complete the reCAPTCHA verification to continue.';
      }
    }
  }

  btnBackToLogin(): void {
    this.router.navigate(['user', 'login']);
  }

  runPasswordValidation(e: any): void {
    const value = e.event.target.value;
    this.updatePasswordStrength(value);
    this.passwordRules.forEach((rule) => {
      rule.isValid = rule.check(value);
    });
  }

  updatePasswordStrength(passValue: string): void {
    this.passwordStrength = this.passwordHelper.getPasswordStrength(passValue);
    this.passStrengthSuccess.value = this.passwordStrength;
  }

  passwordComparison = () => {
    return this.resetPasswordParameterDto.newPassword;
  };

  getPasswordStrengthText(value: number): string {
    return 'Password Strength ' + value * 100 + '%';
  }

  private doResetPassword(): Promise<void> {
    return new Promise((resolve) => {
      this.loadingVisible = true;
      RxjsUtils.safeSubscribe(
        this.userService.resetPassword(this.resetPasswordParameterDto),
        (result: ResultStatusDto) => {
          resolve();
          this.loadingVisible = false;
          if (!!result && result.isSuccess) {
            // Show inline success page instead of popup
            this.isPasswordReset = true;
            this.showErrorMessage = false;
            this.errorMessage = '';
          } else {
            // Show inline error message instead of popup
            this.showErrorMessage = true;
            this.errorMessage =
              !!result && !!result.message
                ? result.message
                : 'Your password reset failed. Try again later';
          }
        },
        (error) => {
          resolve();
          this.loadingVisible = false;
          // Show inline error message instead of popup
          this.showErrorMessage = true;
          this.errorMessage = 'Your password reset failed. Try again later';
        }
      );
    });
  }

  private initializePasswordRules(): void {
    this.passwordRules = [
      {
        label: '8 characters.',
        isValid: false,
        check: (value: string) => value.length >= 8
      },
      {
        label: 'One lowercase (a-z) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasLowerCase(value)
      },
      {
        label: 'One uppercase (A-Z) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasUpperCase(value)
      },
      {
        label: 'One number (0-9) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasNumeric(value)
      },
      {
        label: `Special characters (${this.passwordSpecialCharactersAllowable}).`,
        isValid: false,
        check: (value: string) => this.passwordHelper.hasSpecialCharacter(value)
      }
    ];
  }
}
